import type { BBox, DetectionItem } from '@EcamV2/api';
import { findBestDetection, normalizePlateText, warpedBoxToBBox } from '../utils/bbox-utils';
import type { VehicleDetection } from './VehicleImageWithBbox';

/**
 * Min IoU (intersection-over-union) for two boxes to be considered the same vehicle. Mirrors the
 * BE's `calculateIoU`/`findMatchingDetection`, but with a much tighter threshold than the BE's
 * own 0.7 default: the BE picks among detections already narrowed to one photo instant, while
 * here the reference can be a different photo entirely (nearest evidence in time), so a looser
 * threshold would risk matching a different, nearby-parked vehicle.
 *
 * 0.9 approximates the old per-corner "≤3% of box size" rule it replaced: the loosest case that
 * rule still accepted (every corner drifting the full 3%, e.g. a uniform 3%-per-side shrink or a
 * diagonal translation) works out to IoU ≈ 0.88–0.89, so 0.9 sits just above that boundary.
 * 0.97 would be considerably STRICTER than the old rule, not equivalent to it.
 */

/** A known-good detection of the tracked vehicle (from an entry/grace/exit evidence photo). */
export interface TrackReference {
    capturedAt: number;
    plateText: string | null;
    vehicleBbox: BBox | null;
}

/**
 * Picks the one detection that belongs to the tracked vehicle, so a photo with multiple cars in
 * frame only shows the one relevant to this track:
 *  1. Plate text equal to a known track plate.
 *  2. Vehicle box closest (in time) to a reference box, accepted only when IoU > IOU_MATCH_THRESHOLD.
 *     When the track has no known plate at all, this coordinate fallback is only trusted for
 *     photos captured between entry and exit (`isWithinTrackWindow`) — outside that window
 *     there's no other signal to confirm it's the same vehicle.
 * Returns null when neither matches, or when the photo has no raw detections to pick from at all
 * — caller should render no box for that photo.
 */
export function pickTrackedDetection(
    detections: DetectionItem[],
    refs: TrackReference[],
    photoCapturedAtMs: number,
    isWithinTrackWindow: boolean
): VehicleDetection | null {
    if (detections.length === 0) return null;

    const refPlateTexts = new Set(refs.map((r) => r.plateText).filter((t): t is string => !!t));

    const plateMatch = detections.find((d) => d.text && refPlateTexts.has(normalizePlateText(d.text)));
    if (plateMatch) {
        const plateBbox = warpedBoxToBBox(plateMatch.warpedBox || []);
        return {
            vehicleBbox: warpedBoxToBBox(plateMatch.car?.warpedBox || []) ?? plateBbox,
            plateBbox,
            plateText: plateMatch.text,
            vehicleConfidence: plateMatch.car?.confidence,
        };
    }

    if (refPlateTexts.size === 0 && !isWithinTrackWindow) return null;

    const boxRefs = refs.filter((r) => r.vehicleBbox);
    if (boxRefs.length === 0) return null;
    const nearestRef = boxRefs.reduce((closest, r) =>
        Math.abs(r.capturedAt - photoCapturedAtMs) < Math.abs(closest.capturedAt - photoCapturedAtMs) ? r : closest
    );
    if (!nearestRef.vehicleBbox) return null;

    return findBestDetection(detections, nearestRef.vehicleBbox);
}
